<template>
  <DeleteConfirmationModal
    :bodyText="customBodyText || ''"
    cancelText="cancel"
    :confirmationInputConfig="confirmationInputConfig"
    continueText="delete"
    :enableMainAction="false"
    :handleCancel="hideModal"
    :handleContinue="action"
    :headerText="customHeaderText || headerText"
    :mainActionAttention="true"
    :showLoadingOverlay="showLoadingOverlay"
  >
    <template
      v-if="!customBodyText"
      #message
    >
      <p data-test-id="DeleteConfirmationNumberModal__selectedItems">
        <strong>{{ selectedItems.length }}</strong>{{ subHeadingText }}
      </p>
      <p data-test-id="DeleteConfirmationNumberModal__confirmation">
        <strong>Are you sure</strong>{{ bodyText }}
      </p>
      <p
        v-if="managers.length"
        data-test-id="DeleteConfirmationNumberModal__manager"
      >
        {{ managerText }}
      </p>
    </template>
    <template
      v-if="managers.length > 0"
      v-slot:default
    >
      <div :class="$style.usersContainer">
        <ul :class="$style.usersList">
          <li
            v-for="manager in managers"
            :key="manager.id"
          >
            {{ manager.name }} <span :class="$style.userListEmail">({{ manager.email }})</span>
          </li>
        </ul>
      </div>
    </template>
  </DeleteConfirmationModal>
</template>

<script>
import { ItemTypeConsts, ItemTypeDisplayNames, StoreModuleMap } from 'client/util/Constants';
import { extend } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import DeleteConfirmationModal from 'components/Modal/ModalComponents/DeleteConfirmationModal.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import pluralize from 'pluralize';

const SystemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];

export default {
  name: 'DeleteConfirmationNumberModal',

  components: {
    DeleteConfirmationModal,
  },

  props: {
    action: {
      required: true,
      type: Function,
    },
    customBodyText: {
      default: '',
      type: String,
    },
    customConfirmationText: {
      default: '',
      type: String,
    },
    customHeaderText: {
      default: '',
      type: String,
    },
    isLoading: {
      default: false,
      type: Boolean,
    },
    itemType: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      displayName: ItemTypeDisplayNames[this.itemType],
      managers: [],
      showLoadingOverlay: false,
    };
  },

  computed: {
    /**
     * @description Main body text for the dialog
     */
    bodyText() {
      const selectedCount = this.selectedItems.length;
      const pronoun = pluralize('this', selectedCount);
      const targetTypeName = pluralize(this.displayName, selectedCount);

      return ` you would like to delete ${pronoun} ${targetTypeName}? Deletion will permanently remove all ${this.displayName} data and associations. This action may not be undone.`;
    },

    /**
     * @description Configuration for validation input field
     */
    confirmationInputConfig() {
      const selectedCount = this.selectedItems.length;
      const customErrorMessage = `Does not match the number of ${pluralize(this.displayName)} selected.`;
      const confirmationText = this.customConfirmationText || `Please confirm the number of ${pluralize(this.displayName)} to be deleted.`;

      return {
        autocomplete: 'off',
        autofocus: true,
        confirmationText,
        betweenCustomMessage: customErrorMessage,
        maxCustomMessage: customErrorMessage,
        minCustomMessage: customErrorMessage,
        name: `Delete ${pluralize(this.displayName)}`,
        numericCustomMessage: customErrorMessage,
        requiredCustomMessage: customErrorMessage,
        validationRule: `required|numeric|between:${selectedCount},${selectedCount}`,
        type: 'number',
      };
    },

    headerText() {
      return `Delete ${pluralize(this.displayName)}`;
    },

    managerText() {
      const manager = pluralize('manager', this.managers.length);
      const managerCount = pluralize('user', this.managers.length);
      const managerPronoun = pluralize('this', this.managers.length);
      const toBe = pluralize('is', this.managers.length);
      const a = this.managers.length > 1 ? '' : 'a';

      return `The ${managerCount} listed below ${toBe} ${a} ${manager}. By deleting ${managerPronoun} ${manager}, their direct reports will no longer have a manager attribute populated.`;
    },

    subHeadingText() {
      const selectedCount = this.selectedItems.length;
      const targetTypeName = pluralize(this.displayName, selectedCount);

      return ` ${targetTypeName} selected for deletion.`;
    },
  },

  beforeCreate() {
    const { itemType } = this.$options.propsData;
    const storeModule = StoreModuleMap[itemType];

    extend(
      this.$options.computed,
      mapGetters(storeModule, {
        selectedItems: 'getSelectedItems',
      }),
    );
  },

  created() {
    const { itemType } = this.$options.propsData;
    if (itemType === ItemTypeConsts.systemUser) {
      this.fetchManagers();
    }
  },

  methods: {
    ...mapActions(SystemUserCollection, {
      managerUsers: 'listManagerUsers',
    }),

    async fetchManagers() {
      this.showLoadingOverlay = true;
      this.managers = await this.managerUsers({ ids: this.selectedItems });
      this.showLoadingOverlay = false;
    },

    hideModal() {
      ModalEventBus.close();
    },
  },
};
</script>
<style module>
  @value (jcGrey100, jcGrey400) from '~css/brand-colors.css';

  .usersContainer {
    background-color: jcGrey100;
    border-radius: 4px;
    max-height: 183px;
    overflow-y: scroll;
    padding: 8px 8px 5px;
    scrollbar-color: jcGrey400 jcGrey100;
    scrollbar-width: thin;
    width: 552px;
  }

  .usersContainer::-webkit-scrollbar,
  .usersContainer::-webkit-scrollbar-track {
    background-color: jcGrey100;
    width: 8px;
  }

  .usersContainer::-webkit-scrollbar-thumb {
    background-color: jcGrey400;
    border-radius: 4px;
  }

  .usersList {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .userListEmail {
    color: jcGrey400;
  }
</style>
